import { isEntityRequestType, type TPermissionRequestType, type TPlanRequest } from './entityPlan';
import { criteriaFromRequestType, fitsCriteria } from './requestCriteria';
import { criteriaAsksLabel } from './requestAsksLabel';

/** A request as the open-request surfaces read it: the Home banner and the Requests tab. */
export type TOpenableRequest = TPlanRequest & { createdDateTime?: string };

/** Creation time in epoch ms; an absent or unreadable date counts as the earliest. */
const createdAt = (request: TOpenableRequest): number => {
  const time = Date.parse(request.createdDateTime ?? '');
  return Number.isNaN(time) ? Number.NEGATIVE_INFINITY : time;
};

/**
 * The open requests, latest created first: the order the Home banner steps through. Closed
 * requests (granted or discarded) are dropped. A request with no readable date goes last and is
 * kept; requests created at the same time keep their given order. The given list is not changed.
 */
export function orderOpenRequests<T extends TOpenableRequest>(requests: T[] | undefined): T[] {
  return (requests ?? [])
    .filter((request) => !request.completedDateTime)
    .sort((a, b) => {
      const timeOfA = createdAt(a);
      const timeOfB = createdAt(b);
      if (timeOfA === timeOfB) {
        return 0;
      }
      return timeOfA < timeOfB ? 1 : -1;
    });
}

/** Individual and the seven entity types: the requests that ask for a type. Legacy asks for none. */
const isTypedRequestType = (
  requestType: string | undefined,
): requestType is TPermissionRequestType =>
  requestType === 'Individual' || isEntityRequestType(requestType);

/**
 * What a typed request asks for: the as-built label of its entity type, or the plan row label for
 * an Individual request (the label the request choose header uses, `criteriaAsksLabel`). A Legacy
 * request asks for any identity and has no label, and nor does an unknown type: both read as
 * Legacy, as the rest of the portal reads them.
 */
export function requestAsksLabel(requestType: string | undefined): string | undefined {
  if (!isTypedRequestType(requestType)) {
    return undefined;
  }
  return criteriaAsksLabel(criteriaFromRequestType(requestType));
}

/** The fields of a wallet identity (`GET /v1/customer/me`) the draft check reads. */
export type TWalletIdentity = { entityType?: string; entityStatus?: string };

/**
 * True when the wallet holds a Draft of the type a request asks for, so the customer has begun it
 * and the request card offers Continue, not Start. An Individual request is met by either
 * Individual type. A Legacy or unknown request asks for no type, so no Draft answers it.
 */
export function walletHoldsDraftFor(
  requestType: string | undefined,
  identities: TWalletIdentity[] | undefined,
): boolean {
  if (!isTypedRequestType(requestType)) {
    return false;
  }
  const criteria = criteriaFromRequestType(requestType);
  return (identities ?? []).some(
    (identity) =>
      identity.entityStatus === 'Draft' && fitsCriteria(identity.entityType ?? '', criteria),
  );
}
